import { Switch } from "@headlessui/react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import AnimateHeight, { Height } from "react-animate-height";
import { __ } from "@common/helpers/i18n";
import { SectionToggleProps } from "@common/types";
import { SwitchPrimitive } from "@app/components/ui/switch";
import { useIsRtl } from "@app/hooks";
import { StatusBadge } from "../text";

export function SectionToggle({
  icon,
  hint,
  title,
  className,
  children,
  padding = "medium",
  defaultOpen,
  disabled,
  onOpenChange,
  ...rest
}: SectionToggleProps & Omit<React.HTMLAttributes<HTMLDivElement>, "title">) {
  const isRtl = useIsRtl();
  const [height, setHeight] = useState<Height>(defaultOpen ? "auto" : 0);
  const isOpen = height === "auto";

  useEffect(() => {
    setHeight(!disabled && defaultOpen ? "auto" : 0);
  }, [defaultOpen, disabled]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  return (
    <div
      className={classNames(
        "relative overflow-hidden rounded-lg border bg-card text-card-foreground transition-all duration-150",
        isOpen
          ? "border-primary/25 shadow-soft"
          : "border-border hover:border-foreground/20",
        disabled && "opacity-60 hover:border-border hover:bg-card",
        className,
      )}
      {...rest}
    >
      <Switch.Group as="div">
        <Switch.Label
          as="div"
          className={classNames(
            "block select-none px-4 py-4",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              {icon && (
                <div
                  className={classNames(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-md ring-1 transition-colors [&_svg]:h-5 [&_svg]:w-5",
                    isOpen
                      ? "bg-primary/10 text-primary ring-primary/15"
                      : "bg-muted/40 text-muted-foreground ring-border",
                  )}
                >
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <h3 className="min-w-0 truncate text-sm font-bold leading-6 text-foreground">
                    {title}
                  </h3>
                  {isOpen && (
                    <StatusBadge tone="primary" size="sm">
                      {__("Active")}
                    </StatusBadge>
                  )}
                </div>
                {hint && (
                  <p className="mt-0.5 max-w-3xl truncate text-xs font-medium leading-5 text-muted-foreground">
                    {hint}
                  </p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <SwitchPrimitive
                disabled={disabled}
                checked={isOpen}
                onChange={() => setHeight(isOpen ? 0 : "auto")}
                inactiveClassName="bg-muted-foreground/45 hover:bg-muted-foreground/55"
                size="section"
              />
              <div
                className={classNames(
                  "flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition",
                  isOpen && "bg-muted/50 ltr:rotate-90 rtl:-rotate-90",
                )}
              >
                {isRtl ? (
                  <IconChevronLeft size={18} stroke={2} />
                ) : (
                  <IconChevronRight size={18} stroke={2} />
                )}
              </div>
            </div>
          </div>
        </Switch.Label>
      </Switch.Group>
      <AnimateHeight
        duration={300}
        height={height} // see props documentation below
        contentClassName={classNames(
          "border-t border-border bg-card",
          padding === "large"
            ? "px-6 py-6"
            : padding === "medium"
              ? "px-4 py-4"
              : padding === "small"
                ? "px-3 py-3"
                : "",
        )}
      >
        {children}
      </AnimateHeight>
    </div>
  );
}
